import "./style.css"

import React, { useEffect, useState, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import { IoMdApps, IoMdMenu, IoMdClose, IoMdMore } from "react-icons/io";
import { ArrowLeft } from 'lucide-react';
import ButtonBasic from "../../atoms/buttonBasic/view";
import { listener } from "../../../utils/dispatch";
import { devicecontext } from "../../../context/device/context";
import { ButtonGroupContext } from "./context";

/**
 * ButtonGroup - Molecule that acts as a Page Header with a back button,
 * title/description, and a dropdown menu for actions.
 */
function ButtonGroup(props) {
    const [isOpen, setIsOpen] = useState(false);
    const [showActions, setShowActions] = useState(false);
    const actionsMenuRef = useRef(null);
    const { isMobileDevice } = useContext(devicecontext)
    const isMobile = isMobileDevice()

    useEffect(() => {
        listener('close-group-menu', handleCloseMenu);

        const handleClickOutside = (event) => {
            if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target)) {
                // If the click is on a modal or a portalled element (like MUI DatePicker), 
                // we don't want to close the actions menu because that would unmount the modal.
                const isInsideModal = event.target.closest('.modal') ||
                    event.target.closest('.modal-box') ||
                    event.target.closest('.confirm-overlay') ||
                    event.target.closest('.MuiPopover-root') ||
                    event.target.closest('.MuiDialog-root') ||
                    event.target.closest('.MuiPopper-root') ||
                    event.target.closest('.MuiPickersPopper-root') ||
                    event.target.closest('.MuiPickerStaticWrapper-root') ||
                    event.target.closest('[class*="Mui"]') ||
                    event.target.closest('[class*="picker"]') ||
                    event.target.closest('[role="presentation"]') ||
                    event.target.closest('[role="dialog"]') ||
                    !document.body.contains(event.target);

                if (isInsideModal) return;

                setShowActions(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    function handleCloseMenu() {
        setIsOpen(false);
        setShowActions(false);
    }

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const toggleActions = () => {
        setShowActions(!showActions);
    };

    const goBack = () => {
        if (props.onBack) {
            props.onBack();
        } else {
            window.location.replace('/hub');
        }
    };

    const contextValue = {
        isInGroup: true,
        isMobile: isMobile
    };

    const renderItems = (children) => {
        return React.Children.map(children, child => {
            if (!React.isValidElement(child)) return child;

            // Flatten fragments to ensure each child gets its own menu item wrapper
            if (child.type === React.Fragment) {
                return renderItems(child.props.children);
            }

            return (
                <div className="actions-menu-item">
                    {React.cloneElement(child, {
                        className: (child.props.className || '') + ' menu-item-reset'
                    })}
                </div>
            );
        });
    };

    if (isMobile) {
        const mobileMenu = (
            <div className="mobile-menu-container">
                <ButtonBasic onClick={toggleMenu} className="menu-toggle-button">
                    {isOpen ? <IoMdClose /> : <IoMdMenu />}
                </ButtonBasic>
                {isOpen && (
                    <div className="mobile-menu-overlay">
                        <div className="mobile-menu-content">
                            <ButtonBasic onClick={goBack}><IoMdApps />EasyHub</ButtonBasic>
                            <ButtonGroupContext.Provider value={contextValue}>
                                {renderItems(props.children)}
                            </ButtonGroupContext.Provider>
                        </div>
                    </div>
                )}
            </div>
        );
        return ReactDOM.createPortal(mobileMenu, document.body);
    }

    return (
        <div className="button-group-container">
            <div className="button-group-left">
                <div className="header-text-group">
                    <h1 className="header-title">{props.title || 'Directorio'}</h1>
                    {props.description !== undefined ? (
                        props.description ? <div className="header-subtitle">{props.description}</div> : null
                    ) : (
                        <p className="header-subtitle">Administra tu información aquí</p>
                    )}
                </div>
            </div>

            <div className="button-group-right" ref={actionsMenuRef}>
                <button className={`actions-trigger-button ${showActions ? 'active' : ''}`} onClick={toggleActions}>
                    <IoMdMenu size={24} />
                </button>

                {showActions && (
                    <div className="actions-dropdown">
                        <div className="dropdown-title">{props.title || 'Opciones'}</div>
                        <div className="dropdown-divider"></div>
                        <div className="dropdown-items-container">
                            <ButtonGroupContext.Provider value={contextValue}>
                                {renderItems(props.children)}
                            </ButtonGroupContext.Provider>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ButtonGroup;
